import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AuthService } from '../../services/AuthService';
import { useNavigate } from 'react-router-dom';
import StudentRegistration from './StudentRegistration';
import ForgotPassword from './ForgotPassword';

/**
 * Login Component: Centered secure login card with structured information area
 * matching the approved layout specifications and enforcing deterministic role-based redirection,
 * augmented with robust loading safeguards and safe context token handling.
 */
export const Login = () => {
  const [currentView, setCurrentView] = useState('login'); // 'login', 'register', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email/username and password.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await AuthService.login({ username: email.trim(), password });
      
      if (result.success && result.user) {
        // Pass user object and tokens securely to AuthContext login provider
        login(result.user, result.token || result.accessToken, result.refreshToken);
        
        // Deterministic Portal Routing based on user role
        const role = (result.user.role || '').toLowerCase();
        if (role === 'admin') {
          navigate('/admin/portal', { replace: true });
        } else if (role === 'teacher') {
          navigate('/teacher/analytics', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        setError(result.message || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (currentView === 'register') {
    return (
      <StudentRegistration 
        onSwitchToLogin={() => setCurrentView('login')} 
        onRegistrationSuccess={() => setCurrentView('login')}
      />
    );
  }

  if (currentView === 'forgot') {
    return (
      <ForgotPassword onBackToLogin={() => setCurrentView('login')} />
    );
  }

  return (
    <main className="login-page font-sans">

      {/* LOGIN CARD */}
      <section className="login-card" aria-labelledby="login-title">
        
        {/* Section 1 — Secure Login Header */}
        <header className="login-header">
          <div className="security-icon" aria-hidden="true">🔒</div>
          <h1 id="login-title">Secure Login</h1>
          <p className="security-message">Your information is encrypted and protected.</p>
        </header>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded mb-4" role="alert">
            {error}
          </div>
        )}

        {/* Section 2 — Authentication Form */}
        <form onSubmit={handleLogin} className="login-form" noValidate>
          <div className="form-group">
            <label htmlFor="username">Email Address or Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              placeholder="Enter your email or username"
              required
              aria-required="true"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Enter your password"
                required
                aria-required="true"
                disabled={isLoading}
              />
              <button
                type="button"
                id="togglePassword"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                disabled={isLoading}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="form-options">
            <label className="remember-option">
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
              />
              <span>Remember me</span>
            </label>

            <button
              type="button"
              onClick={() => setCurrentView('forgot')}
              className="text-link bg-transparent border-none cursor-pointer"
              disabled={isLoading}
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            id="signInButton"
            className="primary-button"
            disabled={isLoading}
          >
            {isLoading ? 'SIGNING IN...' : 'SIGN IN'}
          </button>
        </form>

        {/* Section 3 — Student Registration */}
        <div className="registration-section">
          <p>New to LLAP?</p>
          <button
            type="button"
            onClick={() => setCurrentView('register')}
            className="secondary-button"
            disabled={isLoading}
          >
            Create Student Account
          </button>
        </div>

        {/* Section 4 — Teacher Profile CTA */}
        <div className="teacher-profile-cta">
          <button
            type="button"
            onClick={() => navigate('/teacher-profile')}
            className="profile-button w-full"
            disabled={isLoading}
          >
            Meet Your Instructor →
          </button>
        </div>

      </section>

      {/* Section 5 — Information Area */}
      <section className="information-area">
        
        <div className="info-section">
          <h2>Our Mission</h2>
          <p>Helping learners improve their English through interactive, engaging, and learner-centred education.</p>
        </div>

        <div className="info-section">
          <h2>Powered by</h2>
          <h3>Mrs. Shahd Abulila</h3>
          <p>English Language Educator</p>
          <p className="expertise">Cambridge • IELTS • Curriculum Design</p>
        </div>

        <div className="info-section">
          <h2>Need Help?</h2>
          <address>
            <p><a href="mailto:shahdabulila03@gmail.com">📧 shahdabulila03@gmail.com</a></p>
            <p><a href="tel:+601111556245">📞 +60 1111 556245</a></p>
            <p>📍 Kuala Lumpur, Malaysia</p>
          </address>
        </div>

      </section>

      <footer className="site-footer">
        © 2026 LLAP Learning Academy
      </footer>

    </main>
  );
};

export default Login;